import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, addDays, isBefore, isValid } from 'date-fns';
import { CalendarDaysIcon} from '@heroicons/react/24/outline';
import { EventData } from '../../api_types';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { courtApi, eventApi } from '../../api';
import { Court } from '../../api_types';

interface UpcomingEventsDashboardProps {
  facilityId: string;
  businessId: string;
}

// Extended EventData with display properties
interface EnhancedEventData extends EventData {
  displayDate: Date;
  courtNames: string[];
  endDateTime?: Date;
}

// Cache implementation for events and courts
interface CacheData {
  timestamp: number;
  data: any;
}

const cache: { [key: string]: CacheData } = {};
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCachedData = (key: string) => {
  const cachedItem = cache[key];
  if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_EXPIRY) {
    return cachedItem.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache[key] = {
    timestamp: Date.now(),
    data,
  };
};

// Export cache invalidation function to be used from other components
export const invalidateEventsCache = (facilityId: string) => {
  delete cache[`events_${facilityId}`];
};

// Add getEventEndDateTime function before the component
function getEventEndDateTime(event: EventData) {
  try {
    const date = parseISO(event.date);
    if (!isValid(date)) {
      return null;
    }

    const [hours, minutes] = event.endTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    const dateTime = new Date(date);
    dateTime.setHours(hours, minutes, 0, 0);
    return dateTime;
  } catch (error) {
    console.error('Error in getEventEndDateTime:', error);
    return null;
  }
}

const UpcomingEventsDashboard: React.FC<UpcomingEventsDashboardProps> = ({ facilityId, businessId }) => {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState<EnhancedEventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courtMap, setCourtMap] = useState<{[id: string]: string}>({});

  // Fetch courts data with caching
  const fetchCourtsData = async () => {
    try {
      const cacheKey = `courts_${facilityId}`;
      let courtsData = getCachedData(cacheKey);
      
      if (!courtsData) {
        courtsData = await courtApi.getCourts(businessId, facilityId);
        setCachedData(cacheKey, courtsData);
      }

      const courtMapData: {[id: string]: string} = {};
      courtsData.forEach((court: Court) => {
        courtMapData[court.id] = court.name;
      });
      setCourtMap(courtMapData);
      return courtMapData;
    } catch (err) {
      console.error('Error fetching courts:', err);
      throw err;
    }
  };

  // Fetch events data with caching
  const fetchEventsData = async () => {
    const cacheKey = `events_${facilityId}`;
    let eventsData = getCachedData(cacheKey);
    
    if (!eventsData) {
      eventsData = await eventApi.getFacilityEvents(businessId, facilityId);
      setCachedData(cacheKey, eventsData);
    }
    
    return eventsData;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!facilityId) return;
      
      try {
        setLoading(true);
        // Process events and courts in parallel
        const [courtMapData, eventsData] = await Promise.all([
          fetchCourtsData(),
          fetchEventsData()
        ]);
        
        // Filter and prepare upcoming events
        const now = new Date();
        const nextTwoWeeks = addDays(now, 14);
        
        const filteredEvents = eventsData
          .filter((event: EventData) => !event.isBlocked)
          .map((event: EventData) => ({
            ...event,
            displayDate: parseISO(event.date),
            endDateTime: getEventEndDateTime(event),
            courtNames: event.courtIds.map((id: string) => courtMapData[id]).filter(Boolean)
          }))
          .filter((event: EnhancedEventData) => 
            event.endDateTime && 
            event.endDateTime >= now && 
            isBefore(event.displayDate, nextTwoWeeks)
          )
          .sort((a: EnhancedEventData, b: EnhancedEventData) => 
            (a.endDateTime?.getTime() || 0) - (b.endDateTime?.getTime() || 0)
          )
          .slice(0, 3); // Only show 3 events
        
        setUpcomingEvents(filteredEvents);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch events');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [facilityId]);

  const handleViewEvent = (eventId: string) => {
    navigate(`/business/dashboard/events/${eventId}`);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (error) {
    return <div className="p-2 text-red-600 bg-red-50 rounded-lg text-xs">{error}</div>;
  }

  return (
    <div className="bg-white shadow-xl rounded-2xl p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDaysIcon className="h-7 w-7 text-indigo-500" /> Upcoming Events
        </h2>
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                Event
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                Participants
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8">
                  <div className="flex justify-center">
                    <LoadingSpinner size="sm" />
                  </div>
                </td>
              </tr>
            ) : upcomingEvents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No upcoming events
                </td>
              </tr>
            ) : (
              upcomingEvents.map((event, idx) => (
                <tr key={event.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-indigo-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-2">
                      <span>{event.title}</span>
                      <div className="flex flex-wrap gap-1">
                        {[...event.courtNames].sort((a, b) => a.localeCompare(b)).map((courtName, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700"
                          >
                            {courtName}
                          </span>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{format(event.displayDate, 'MMM d, yyyy')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.participants?.length || 0} / {event.capacity || '∞'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewEvent(event.id)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UpcomingEventsDashboard; 